import { isoDate as melDate, todayISO as melToday } from "@/lib/intl";
import { useEffect, useState } from "react";
import { getCached, setCached } from "@/lib/pageStateCache";
import { useOutletContext, Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Printer,
  RefreshCw,
} from "lucide-react";
import { fmtMoney } from "@/lib/intl";
import { cn } from "@/lib/utils";
import { Calendar } from "@/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { format } from "date-fns";
import { toast } from "sonner";

const WEEKDAY = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];

function todayIso() {
  return melToday();
}

function shiftIso(iso, days) {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return melDate(d);
}

function timeOnly(dt) {
  if (!dt || dt.length < 16) return "";
  // Acuity ISO datetimes are timezone-localised already (e.g. 2026-05-08T08:30:00+1000)
  return dt.slice(11, 16);
}

function fullDateLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso + "T00:00:00");
  return `${WEEKDAY[d.getDay()]}, ${format(d, "dd-MMM-yyyy")}`;
}

function StatusPill({ status }) {
  const map = {
    paid: { label: "Paid", cls: "border-emerald-300 bg-emerald-50 text-emerald-800" },
    credit: { label: "Credit", cls: "border-blue-300 bg-blue-50 text-blue-800" },
    outstanding: { label: "Outstanding", cls: "border-rose-300 bg-rose-50 text-rose-800" },
    partial: { label: "Partial", cls: "border-amber-300 bg-amber-50 text-amber-900" },
    free: { label: "Free", cls: "border-zinc-300 bg-zinc-100 text-zinc-700" },
  };
  const m = map[status] || { label: status, cls: "border-zinc-300 bg-zinc-50 text-zinc-700" };
  return (
    <span
      className={cn(
        "inline-block text-2xs font-semibold uppercase tracking-wider px-2 py-0.5 rounded border",
        m.cls
      )}
    >
      {m.label}
    </span>
  );
}

function StatTile({ label, value, hint, intent }) {
  return (
    <div
      className={cn(
        "px-3 py-2 rounded-md border",
        intent === "danger" ? "border-rose-200 bg-rose-50/50" : "border-zinc-200 bg-white"
      )}
    >
      <div className="text-2xs uppercase tracking-[0.18em] text-zinc-500">{label}</div>
      <div
        className={cn(
          "mt-0.5 font-mono-num text-xl tracking-tight",
          intent === "danger" ? "text-rose-800" : "text-zinc-950"
        )}
      >
        {value}
      </div>
      {hint && <div className="mt-0.5 text-2xs text-zinc-500 font-mono-num">{hint}</div>}
    </div>
  );
}

export default function ScheduleSheet() {
  const { status } = useOutletContext();
  const cached = getCached("schedule-sheet");
  const [date, setDate] = useState(cached?.date ?? todayIso());
  const [data, setData] = useState(cached?.data ?? null);
  const [loading, setLoading] = useState(false);
  const [hasFetched, setHasFetched] = useState(Boolean(cached?.data));
  const [error, setError] = useState(null);
  // viewMode: "manager" (full sheet incl. revenue) or "<calendar_id>" (single instructor, no revenue)
  const [viewMode, setViewMode] = useState(cached?.viewMode ?? "manager");

  // Persist on any change so navigating back keeps the date + loaded sheet.
  useEffect(() => {
    setCached("schedule-sheet", { date, data, viewMode, ts: Date.now() });
  }, [date, data, viewMode]);

  async function fetchSheet(iso) {
    if (!status?.configured) return;
    setLoading(true);
    setError(null);
    try {
      const r = await api.get("/schedule-sheet", { params: { date: iso }, timeout: 60000 });
      setData(r.data);
      setHasFetched(true);
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      setError(msg);
      toast.error("Failed to load schedule", { description: msg });
    } finally {
      setLoading(false);
    }
  }

  // Reset the "loaded" state when the date changes so the user has to click
  // Apply again — keeps the page from auto-fetching on mount or date change.
  useEffect(() => {
    setData(null);
    setHasFetched(false);
    setError(null);
  }, [date]);

  if (!status?.configured) {
    return (
      <Card className="p-12 rounded-lg border-2 border-dashed border-zinc-200 bg-zinc-50 text-center">
        <h2 className="font-display text-2xl font-semibold tracking-tight text-zinc-950">
          Connect Acuity to view the daily schedule
        </h2>
        <Link to="/settings">
          <Button
            className="mt-5 bg-zinc-950 hover:bg-zinc-800 text-white"
            data-testid="schedule-goto-settings"
          >
            Open Settings
          </Button>
        </Link>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="schedule-sheet-page">
      {/* Toolbar — hidden on print */}
      <div className="print:hidden flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.2em] text-zinc-500">
            Daily schedule
          </div>
          <h1 className="mt-1 font-display text-2xl sm:text-3xl font-bold tracking-tight text-zinc-950">
            {fullDateLabel(date)}
          </h1>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* View-mode picker */}
          {data?.instructors?.length > 0 && (
            <select
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              className="h-9 rounded-md border border-zinc-300 bg-white px-2.5 text-sm hover:bg-zinc-50 focus:outline-none focus:ring-2 focus:ring-zinc-500"
              data-testid="schedule-view-mode"
              title="Manager view shows revenue totals; instructor view hides them and filters to one calendar"
            >
              <option value="manager">Manager — full schedule</option>
              <optgroup label="Instructor view (no revenue)">
                {data.instructors.map((g) => (
                  <option key={g.calendar_id} value={g.calendar_id}>
                    {g.instructor}
                  </option>
                ))}
              </optgroup>
            </select>
          )}

          <Button
            variant="outline"
            size="sm"
            className="border-zinc-300 hover:bg-zinc-100"
            onClick={() => setDate(shiftIso(date, -1))}
            data-testid="schedule-prev-day"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <DatePopover value={date} onChange={setDate} />

          <Button
            variant="outline"
            size="sm"
            className="border-zinc-300 hover:bg-zinc-100"
            onClick={() => setDate(shiftIso(date, 1))}
            data-testid="schedule-next-day"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>

          <Button
            variant="outline"
            size="sm"
            className="border-zinc-300 hover:bg-zinc-100"
            onClick={() => setDate(todayIso())}
            data-testid="schedule-today-btn"
          >
            Today
          </Button>

          <Button
            variant={hasFetched ? "outline" : "default"}
            size="sm"
            className={cn(
              hasFetched
                ? "border-zinc-300 hover:bg-zinc-100"
                : "bg-zinc-950 hover:bg-zinc-800 text-white",
            )}
            onClick={() => fetchSheet(date)}
            disabled={loading}
            data-testid="schedule-refresh-btn"
          >
            <RefreshCw className={cn("h-4 w-4", loading && "animate-spin", !hasFetched && "mr-1.5")} />
            {!hasFetched && "Apply"}
          </Button>

          <Button
            size="sm"
            className="bg-zinc-950 hover:bg-zinc-800 text-white"
            onClick={() => window.print()}
            disabled={!data}
            data-testid="schedule-print-btn"
          >
            <Printer className="h-4 w-4 mr-1.5" />
            Print
          </Button>
        </div>
      </div>

      {/* Print-only header */}
      <div className="hidden print:block">
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">Daily schedule</div>
        <h1 className="font-display text-2xl font-bold text-zinc-950">{fullDateLabel(date)}</h1>
      </div>

      {error && (
        <div
          data-testid="schedule-error"
          className="p-4 rounded-md border border-red-200 bg-red-50 text-sm text-red-700 print:hidden"
        >
          {error}
        </div>
      )}

      {!hasFetched && !loading && !error && (
        <Card
          className="p-10 sm:p-12 rounded-lg border-2 border-dashed border-zinc-200 bg-zinc-50 print:hidden"
          data-testid="schedule-empty"
        >
          <div className="flex flex-col items-center text-center max-w-md mx-auto">
            <div className="h-12 w-12 grid place-items-center rounded-full bg-white border border-zinc-200">
              <CalendarIcon className="h-5 w-5 text-zinc-500" />
            </div>
            <h2 className="mt-4 font-display text-xl font-semibold text-zinc-950">
              Pick a date, then Apply
            </h2>
            <p className="mt-2 text-sm text-zinc-600">
              The schedule is pulled from Acuity on demand. Choose a date above and click
              <span className="font-medium text-zinc-900"> Apply </span> to load it.
            </p>
          </div>
        </Card>
      )}

      {loading && !data && (
        <Card className="p-12 rounded-lg border border-zinc-200 shadow-none text-center print:hidden">
          <RefreshCw className="h-8 w-8 text-zinc-400 mx-auto mb-3 animate-spin" strokeWidth={1.5} />
          <p className="text-sm text-zinc-600">Loading schedule…</p>
        </Card>
      )}

      {data && (() => {
        const isManager = viewMode === "manager";
        const visible = isManager
          ? data.instructors
          : data.instructors.filter((g) => g.calendar_id === viewMode);
        const visibleAppts = visible.reduce((a, g) => a + g.appointment_count, 0);
        const visibleOutstanding = visible.reduce((a, g) => a + g.outstanding, 0);
        return (
          <>
            {/* Day-totals strip — manager view only */}
            {isManager ? (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="schedule-totals">
                <StatTile
                  label="Appointments"
                  value={data.overall.appointments}
                  hint={data.overall.canceled ? `${data.overall.canceled} cancelled` : undefined}
                />
                <StatTile label="Expected revenue" value={fmtMoney(data.overall.expected_revenue)} />
                <StatTile label="Paid revenue" value={fmtMoney(data.overall.paid_revenue)} />
                <StatTile
                  label="Outstanding"
                  value={fmtMoney(data.overall.outstanding)}
                  hint="To be collected today"
                  intent={data.overall.outstanding > 0 ? "danger" : undefined}
                />
              </div>
            ) : (
              <div
                className="grid grid-cols-2 sm:grid-cols-3 gap-3"
                data-testid="schedule-instructor-totals"
              >
                <StatTile
                  label="Your appointments"
                  value={visibleAppts}
                  hint={visible[0]?.instructor}
                />
                <StatTile
                  label="To collect today"
                  value={fmtMoney(visibleOutstanding)}
                  intent={visibleOutstanding > 0 ? "danger" : undefined}
                />
                <StatTile
                  label="Pre-paid / credit"
                  value={
                    visible.reduce(
                      (a, g) => a + g.appointments.filter((x) => x.status === "credit").length,
                      0
                    )
                  }
                  hint="No money owing"
                />
              </div>
            )}

            {visible.length === 0 && (
              <Card
                data-testid="schedule-empty"
                className="p-12 rounded-lg border-2 border-dashed border-zinc-200 bg-zinc-50 text-center"
              >
                <CalendarIcon className="h-8 w-8 text-zinc-300 mx-auto mb-2" strokeWidth={1.5} />
                <p className="font-display text-xl text-zinc-950">
                  {isManager ? "No appointments scheduled" : "No appointments for you"}
                </p>
                <p className="mt-1 text-sm text-zinc-500">
                  Enjoy the day off — or check another date.
                </p>
              </Card>
            )}

            <div className="space-y-3 print:space-y-2">
              {visible.map((g) => (
                <InstructorBlock key={g.calendar_id} group={g} showRevenue={isManager} />
              ))}
            </div>

            <div className="text-2xs text-zinc-400 font-mono-num text-center pt-3 print:pt-1">
              {isManager ? "Manager view · all instructors" : `Instructor view · ${visible[0]?.instructor || ""}`}
              {" · "}generated {data.today}
            </div>
          </>
        );
      })()}

      {/* Print-friendly tweaks */}
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 14mm; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          [data-testid="schedule-sheet-page"] { color: #000; }
          .print\\:hidden { display: none !important; }
          .schedule-instructor { break-inside: avoid; page-break-inside: avoid; }
          .shadow-none, .shadow { box-shadow: none !important; }
        }
      `}</style>
    </div>
  );
}

function InstructorBlock({ group, showRevenue = true }) {
  return (
    <Card className="schedule-instructor rounded-md border border-zinc-300 shadow-none overflow-hidden">
      <div className="px-4 py-2 border-b border-zinc-300 bg-zinc-50 flex flex-wrap gap-3 items-baseline justify-between">
        <div className="flex items-baseline gap-3 flex-wrap">
          <div className="font-display text-base font-semibold tracking-tight text-zinc-950">
            {group.instructor}
          </div>
          {group.location && (
            <div className="text-2xs text-zinc-500">{group.location}</div>
          )}
        </div>
        <div className="flex flex-wrap gap-3 text-2xs font-mono-num text-zinc-700">
          <span>
            <span className="text-zinc-400">Appts</span>{" "}
            <span className="text-zinc-950 font-semibold">{group.appointment_count}</span>
          </span>
          {showRevenue && (
            <>
              <span>
                <span className="text-zinc-400">Expected</span>{" "}
                <span className="text-zinc-950">{fmtMoney(group.expected_revenue)}</span>
              </span>
              <span>
                <span className="text-zinc-400">Paid</span>{" "}
                <span className="text-emerald-700">{fmtMoney(group.paid_revenue)}</span>
              </span>
            </>
          )}
          <span>
            <span className="text-zinc-400">Owing</span>{" "}
            <span className={cn(group.outstanding > 0 ? "text-rose-700 font-semibold" : "text-zinc-700")}>
              {fmtMoney(group.outstanding)}
            </span>
          </span>
        </div>
      </div>

      <table className="w-full text-[0.8125rem]">
        <thead className="bg-white border-b border-zinc-200">
          <tr className="text-left">
            {["Time", "Type", "Customer", "Paid", "Owing", ""].map((h) => (
              <th
                key={h}
                className="px-3 py-1.5 text-2xs font-semibold uppercase tracking-wider text-zinc-500"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {group.appointments.map((a, i) => (
            <tr
              key={a.id || i}
              className={cn(
                "border-b last:border-0 border-zinc-100",
                a.status === "outstanding" && "bg-rose-50/40",
                a.status === "partial" && "bg-amber-50/40",
                a.status === "credit" && "bg-blue-50/30"
              )}
              data-testid={`schedule-appt-${a.id || i}`}
            >
              <td className="px-3 py-1.5 align-top whitespace-nowrap font-mono-num">
                <span className="font-medium text-zinc-950">{timeOnly(a.time)}</span>
                {a.duration && (
                  <span className="ml-1 text-2xs text-zinc-400">{a.duration}m</span>
                )}
              </td>
              <td className="px-3 py-1.5 align-top text-zinc-700">{a.type || "—"}</td>
              <td className="px-3 py-1.5 align-top">
                <div className="text-zinc-950">
                  <span className="font-medium">{a.customer_name}</span>
                  {(a.email || a.phone) && (
                    <span className="text-2xs text-zinc-500 font-mono-num ml-2">
                      {a.email}
                      {a.email && a.phone ? " · " : ""}
                      {a.phone}
                    </span>
                  )}
                </div>
                {a.notes && (
                  <div className="text-2xs italic text-zinc-500 truncate max-w-xl">
                    {a.notes}
                  </div>
                )}
              </td>
              <td className="px-3 py-1.5 align-top font-mono-num text-zinc-900 whitespace-nowrap">
                {a.status === "credit" ? (
                  <span className="text-blue-700 text-2xs font-semibold uppercase tracking-wider">
                    Credit{a.certificate ? ` ${a.certificate}` : ""}
                  </span>
                ) : (
                  fmtMoney(a.amount_paid)
                )}
              </td>
              <td
                className={cn(
                  "px-3 py-1.5 align-top font-mono-num whitespace-nowrap",
                  a.outstanding > 0 ? "text-rose-700 font-semibold" : "text-zinc-400"
                )}
              >
                {a.outstanding > 0 ? fmtMoney(a.outstanding) : "—"}
              </td>
              <td className="px-3 py-1.5 align-top">
                <StatusPill status={a.status} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </Card>
  );
}

function DatePopover({ value, onChange }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="border-zinc-300 hover:bg-zinc-100 font-mono-num"
          data-testid="schedule-date-picker"
        >
          <CalendarIcon className="h-4 w-4 mr-1.5" />
          {value}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0" align="end">
        <Calendar
          mode="single"
          selected={value ? new Date(value + "T00:00:00") : undefined}
          defaultMonth={value ? new Date(value + "T00:00:00") : undefined}
          onSelect={(d) => {
            if (!d) return;
            onChange(melDate(d));
            setOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
}
